import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSafeMode } from '../contexts/SafeModeContext';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageSquare, Users, Clock, TrendingUp, Search,
  ChevronLeft, ChevronRight, X, ExternalLink, Globe
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../services/api';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 365, label: '1 Year' },
];

// Summary cards
const SummaryCards = ({ summary, colors }) => {
  const cards = [
    { label: 'Total Sessions', value: summary?.total_sessions ?? 0, icon: Users, color: '#8b5cf6' },
    { label: 'Total Messages', value: summary?.total_messages ?? 0, icon: MessageSquare, color: '#3b82f6' },
    { label: 'Avg Messages / Session', value: summary?.avg_messages_per_session ?? 0, icon: TrendingUp, color: '#10b981' },
    {
      label: 'Avg Duration',
      value: summary?.avg_duration_seconds != null
        ? summary.avg_duration_seconds < 60
          ? `${summary.avg_duration_seconds}s`
          : `${Math.round(summary.avg_duration_seconds / 60)}m`
        : '0s',
      icon: Clock,
      color: '#f59e0b',
    },
    { label: 'Conversion Rate', value: `${summary?.conversion_rate ?? 0}%`, icon: TrendingUp, color: '#ef4444' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {cards.map((c) => (
        <Card key={c.label} className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className={cn("text-xs font-medium uppercase tracking-wide", colors.textMuted)}>{c.label}</span>
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
            </div>
            <p className={cn("text-2xl font-bold", colors.textPrimary)}>{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Session detail modal
const SessionDetailModal = ({ session, colors, onClose }) => {
  if (!session) return null;

  const formatTime = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  const durationDisplay = session.duration_seconds != null
    ? session.duration_seconds < 60
      ? `${session.duration_seconds}s`
      : `${Math.round(session.duration_seconds / 60)}m ${session.duration_seconds % 60}s`
    : 'N/A';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={cn("w-full max-w-3xl max-h-[85vh] rounded-xl shadow-2xl border flex flex-col", colors.bgCard, colors.borderPrimary)}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
          <div>
            <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Chat Session</h2>
            <p className={cn("text-xs mt-0.5", colors.textMuted)}>{formatTime(session.started_at)}</p>
          </div>
          <button onClick={onClose} className={cn("p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800")}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Metadata */}
        <div className={cn("px-6 py-3 border-b grid grid-cols-2 md:grid-cols-4 gap-3 text-sm", colors.borderPrimary)}>
          <div>
            <span className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Messages</span>
            <p className={cn("font-semibold", colors.textPrimary)}>{session.message_count}</p>
          </div>
          <div>
            <span className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Duration</span>
            <p className={cn("font-semibold", colors.textPrimary)}>{durationDisplay}</p>
          </div>
          <div>
            <span className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Converted</span>
            <p className={cn("font-semibold", session.converted ? "text-green-600" : colors.textPrimary)}>
              {session.converted ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <span className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Referrer</span>
            <p className={cn("font-semibold truncate", colors.textPrimary)}>{session.referrer || 'Direct'}</p>
          </div>
        </div>

        {/* Additional metadata row */}
        <div className={cn("px-6 py-2 border-b text-xs grid grid-cols-1 md:grid-cols-2 gap-2", colors.borderPrimary, colors.textMuted)}>
          {session.ip_address && <div><span className="font-medium">IP:</span> {session.ip_address}</div>}
          {session.user_agent && <div className="truncate"><span className="font-medium">UA:</span> {session.user_agent}</div>}
          {session.utm_params && Object.keys(session.utm_params).length > 0 && (
            <div className="col-span-2">
              <span className="font-medium">UTM:</span> {JSON.stringify(session.utm_params)}
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {(session.messages || []).map((msg, i) => (
            <div
              key={i}
              className={cn(
                "flex",
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  "max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed",
                  msg.role === 'user'
                    ? "bg-blue-600 text-white rounded-br-sm"
                    : cn("rounded-bl-sm", colors.bgTertiary, colors.textPrimary)
                )}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {msg.timestamp && (
                  <p className={cn("text-[10px] mt-1.5 opacity-60")}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
          ))}
          {(!session.messages || session.messages.length === 0) && (
            <p className={cn("text-center py-8", colors.textMuted)}>No messages in this session</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Sessions table
const SessionsTable = ({ sessions, colors, onSelect }) => {
  const formatTime = (ts) => {
    if (!ts) return 'N/A';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={cn("border-b text-left text-xs uppercase tracking-wider", colors.borderPrimary, colors.textMuted)}>
            <th className="px-4 py-3 font-medium">Time</th>
            <th className="px-4 py-3 font-medium">First Question</th>
            <th className="px-4 py-3 font-medium text-center">Msgs</th>
            <th className="px-4 py-3 font-medium">Duration</th>
            <th className="px-4 py-3 font-medium">Referrer</th>
            <th className="px-4 py-3 font-medium text-center">Converted</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => {
            const dur = s.duration_seconds != null
              ? s.duration_seconds < 60 ? `${s.duration_seconds}s` : `${Math.round(s.duration_seconds / 60)}m`
              : '-';
            return (
              <tr
                key={s.session_id || i}
                data-testid={`session-row-${i}`}
                className={cn(
                  "border-b cursor-pointer transition-colors",
                  colors.borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                )}
                onClick={() => onSelect(s)}
              >
                <td className={cn("px-4 py-3 text-sm whitespace-nowrap", colors.textPrimary)}>{formatTime(s.started_at)}</td>
                <td className={cn("px-4 py-3 text-sm max-w-md", colors.textPrimary)}>
                  <p className="truncate">{s.first_question || '-'}</p>
                </td>
                <td className={cn("px-4 py-3 text-sm text-center font-medium", colors.textPrimary)}>{s.message_count}</td>
                <td className={cn("px-4 py-3 text-sm", colors.textMuted)}>{dur}</td>
                <td className={cn("px-4 py-3 text-sm max-w-[140px]", colors.textMuted)}>
                  <p className="truncate">{s.referrer || 'Direct'}</p>
                </td>
                <td className="px-4 py-3 text-center">
                  {s.converted ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Yes</span>
                  ) : (
                    <span className={cn("text-xs", colors.textMuted)}>-</span>
                  )}
                </td>
              </tr>
            );
          })}
          {sessions.length === 0 && (
            <tr>
              <td colSpan={6} className={cn("px-4 py-12 text-center", colors.textMuted)}>
                No chat sessions found for this period
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Main page
export default function MarketIntelligencePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const [period, setPeriod] = useState(30);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [convertedOnly, setConvertedOnly] = useState(false);
  const [minMessages, setMinMessages] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, sessionsRes] = await Promise.all([
        api.get(`/api/admin/market-intelligence/summary?days=${period}`),
        api.get(`/api/admin/market-intelligence/sessions?days=${period}&page=${page}&page_size=${pageSize}&converted_only=${convertedOnly}&min_messages=${minMessages}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
      ]);
      setSummary(summaryRes.data);
      setSessions(sessionsRes.data.sessions);
      setTotal(sessionsRes.data.total);
    } catch (err) {
      console.error('Failed to load market intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [period, page, pageSize, convertedOnly, minMessages, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Market Intelligence</h1>
              <p className={cn("text-sm mt-1", colors.textMuted)}>Analyze public chat sessions and visitor behavior</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Period selector */}
              <select
                value={period}
                onChange={(e) => { setPeriod(Number(e.target.value)); setPage(1); }}
                className={cn("px-3 py-2 rounded-lg border text-sm", colors.bgCard, colors.borderPrimary, colors.textPrimary)}
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary Cards */}
          <SummaryCards summary={summary} colors={colors} />

          {/* Sessions Table Card */}
          <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
                  <MessageSquare className="w-5 h-5" />
                  Chat Sessions
                  <span className={cn("text-sm font-normal", colors.textMuted)}>({total})</span>
                </CardTitle>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Search */}
                  <form onSubmit={handleSearch} className="flex items-center gap-1">
                    <Input
                      data-testid="session-search-input"
                      placeholder="Search questions..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className={cn("w-48 h-8 text-sm", colors.bgTertiary)}
                    />
                    <Button type="submit" size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Search className="w-4 h-4" />
                    </Button>
                    {search && (
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </form>

                  {/* Converted filter */}
                  <label className={cn("flex items-center gap-1.5 text-xs cursor-pointer", colors.textMuted)}>
                    <input
                      type="checkbox"
                      checked={convertedOnly}
                      onChange={(e) => { setConvertedOnly(e.target.checked); setPage(1); }}
                      className="rounded"
                    />
                    Converted only
                  </label>

                  {/* Min messages */}
                  <select
                    value={minMessages}
                    onChange={(e) => { setMinMessages(Number(e.target.value)); setPage(1); }}
                    className={cn("px-2 py-1 rounded border text-xs", colors.bgTertiary, colors.borderPrimary, colors.textPrimary)}
                  >
                    <option value={0}>All messages</option>
                    <option value={2}>2+ messages</option>
                    <option value={3}>3+ messages</option>
                    <option value={5}>5+ messages</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className={cn("text-center py-12", colors.textMuted)}>Loading...</div>
              ) : (
                <>
                  <SessionsTable sessions={sessions} colors={colors} onSelect={setSelectedSession} />

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className={cn("flex items-center justify-between px-4 py-3 border-t", colors.borderPrimary)}>
                      <span className={cn("text-sm", colors.textMuted)}>
                        Page {page} of {totalPages}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page <= 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={page >= totalPages}
                          onClick={() => setPage(p => p + 1)}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        colors={colors}
        onClose={() => setSelectedSession(null)}
      />
    </div>
  );
}
